import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, retry } from 'rxjs';

import { environment } from '../../environments/environment';
import { buildSearchParams } from '../_helpers/utils';
import {
  APIResponse,
  BasicResponse,
  DocumentUploadResponse,
} from '../_types/api-responses.types';
import { PresignedUrlResponse } from '../_types/auth.types';
import {
  DocumentModel,
  DocumentSummary,
  GenerateSignatureRequestRequest,
  GenerateSignatureRequestResponse,
  RequestDocumentSignatureRequest,
  SearchDocsRequest,
  SearchSignedContractDocumentsParams,
  SignedContractDocument,
  UpdateDocumentRequest,
} from '../_types/document.types';
import { DocumentType } from '../enums/document-type.enum';
import { EntityType } from '../enums/entity-type.enum';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  _baseURL: any;

  user: any;

  deviceId: number;

  constructor(
    private httpClient: HttpClient,
    private globaols: Globals
  ) {
    this.deviceId = Date.now();
    this.user = this.globaols.getUser();
    this._baseURL = environment.apiUrl;
  }

  get headers() {
    return new HttpHeaders().set('Access-Token', this.globaols.getUser().token);
  }

  _getUserDocMemberList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/document-store/v1/get/user/docs`,
      obj,
      { headers: this.headers }
    );
  }

  _getUserMemberDoctList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/document-store/v1/get/member/docs`,
      obj,
      { headers: this.headers }
    );
  }

  _fileSingleUpload(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/document-store/v1/upload`,
      obj,
      { headers: this.headers }
    );
  }

  getPresignedUrl(key: string): Observable<APIResponse<PresignedUrlResponse>> {
    return this.httpClient.post<APIResponse<PresignedUrlResponse>>(
      `${this._baseURL}api/document-store/v1/get/presigned`,
      { key },
      {
        headers: this.headers,
      }
    );
  }

  UploadDocument(obj: any): Observable<APIResponse<DocumentUploadResponse>> {
    const form = new FormData();
    form.append('docName', obj.docName);
    form.append('userId', obj.userId);
    if (obj.memberId !== undefined) {
      form.append('memberId', obj.memberId);
    }
    if (obj.uploadedBy !== undefined) {
      form.append('uploadedBy', obj.uploadedBy);
    } else {
      form.append('uploadedBy', this.user.partnerId);
    }
    if (obj.doc !== undefined) {
      form.append('doc', obj.doc, obj.doc.name);
    }
    if (obj.type) {
      form.append('type', obj.type);
    }
    // document-store publishes by default, so only an explicit false matters.
    if (obj.isPublished !== undefined) {
      form.append('published', obj.isPublished);
    }
    if (obj.notifyCustomer) {
      form.append('notifyCustomer', 'true');
    }

    return this.httpClient.post<APIResponse<DocumentUploadResponse>>(
      `${this._baseURL}api/document-store/v1/upload`,
      form,
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Uploads the files and keeps the whole document-store answer for each.
   *
   * Callers that link the uploads to something else need the `documentId`:
   * a proposal's policy and offer papers are recorded by id, not by key.
   */
  uploadDocuments(
    files: File[],
    userId: string,
    memberId: string,
    type?: DocumentType
  ): Observable<DocumentUploadResponse[]> {
    if (files.length === 0) return of([]);

    return forkJoin(
      files.map((file) =>
        this.UploadDocument({
          userId,
          memberId,
          uploadedBy: this.user.partnerId,
          doc: file,
          docName: file.name,
          type,
        }).pipe(
          map((res) => {
            if (res.error) throw new Error(res.error.clientErrorMessage);
            return res.data;
          }),
          retry(3)
        )
      )
    );
  }

  uploadFiles(
    files: File[],
    userId: string,
    memberId: string,
    type?: DocumentType
  ): Observable<string[]> {
    return this.uploadDocuments(files, userId, memberId, type).pipe(
      map((documents) => documents.map((document) => document.key))
    );
  }

  requestDocumentSignature(
    obj: RequestDocumentSignatureRequest
  ): Observable<APIResponse<DocumentUploadResponse>> {
    const form = new FormData();
    form.append('userId', obj.userId);
    form.append('memberId', obj.memberId);
    obj.files.forEach((x) => {
      form.append('files', x, x.name);
    });

    return this.httpClient.post<APIResponse<DocumentUploadResponse>>(
      `${this._baseURL}api/document-store/v1/request-signature`,
      form,
      {
        headers: this.headers,
      }
    );
  }

  updateDocument(
    obj: UpdateDocumentRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/document-store/v1/update`,
      {
        ...obj,
        entityType: EntityType.PARTNER_ID,
        entityId: this.globaols.getUser().partnerId,
      },
      { headers: this.headers }
    );
  }

  sendDocumentSignatureRequest(
    obj: GenerateSignatureRequestRequest
  ): Observable<APIResponse<GenerateSignatureRequestResponse>> {
    return this.httpClient.post<APIResponse<GenerateSignatureRequestResponse>>(
      `${this._baseURL}api/document-store/v1/contract/yousign/generate-request`,
      obj,
      { headers: this.headers }
    );
  }

  getDocument(id: number): Observable<APIResponse<DocumentModel>> {
    return this.httpClient.get<APIResponse<DocumentModel>>(
      `${this._baseURL}api/document-store/v1/get/doc/id/${id}`,
      { headers: this.headers }
    );
  }

  /**
   * One customer's stored documents, most recent first, for a picker.
   *
   * Staff only, and the unpublished ones answer unless the caller opts out:
   * invoices, reminders, signed offers and inbound attachments are stored
   * that way, and a back-office picker wants exactly those. The backend
   * defaults the other way round, for the customer-facing listings.
   */
  searchDocuments(
    body: SearchDocsRequest
  ): Observable<APIResponse<{ documents: DocumentSummary[] }>> {
    return this.httpClient.post<APIResponse<{ documents: DocumentSummary[] }>>(
      `${this._baseURL}api/document-store/v1/get/docs/search`,
      { includeUnpublished: true, ...body },
      { headers: this.headers }
    );
  }

  searchSignedContractDocuments(
    familyMemberId: string,
    params: SearchSignedContractDocumentsParams
  ): Observable<APIResponse<{ documents: SignedContractDocument[] }>> {
    const searchParams = buildSearchParams({
      insuranceType: params.insuranceType,
      contractType: params.contractType,
      searchText: params.searchText,
      limit: params.limit ?? 20,
    });
    return this.httpClient.get<
      APIResponse<{ documents: SignedContractDocument[] }>
    >(
      `${this._baseURL}api/document-store/v1/contract/family-member/${familyMemberId}/signed?${searchParams.toString()}`,
      { headers: this.headers }
    );
  }
}
